import { useCallback, useMemo } from 'react';
import { Params } from 'react-router-dom';
import utils from 'utils';
import useQueryParams from './useQueryParams';
import { EMPTY_ARRAY_STRINGIFIED, FIRST_PAGE } from 'data/global';

export default function useQueryFilter<T extends Params>() {
  const {
    params,
    setQueryParam,
    removeQueryParam,
    removeSeveralQueryParams,
    setParamsAndNavigate,
  } = useQueryParams<T>();

  const activeFilter = useMemo(() => {
    const { page, sort, ...rest } = params;

    return rest;
  }, [params]);

  const hasActiveFilter = useMemo(
    () => Object.values(activeFilter).length > 0,
    [activeFilter],
  );

  const applyQueryFilter = useCallback(
    (key: keyof T, value: string) => {
      if (!value || !value?.length || value === EMPTY_ARRAY_STRINGIFIED)
        removeQueryParam(key);
      else setQueryParam(key, value.toString());
    },
    [removeQueryParam, setQueryParam],
  );

  const onFiltersApply = useCallback(
    (filterKey: string, values: string | string[]) => {
      const queryValues =
        typeof values === 'string' ? values : utils.mapArrayToParams(values);

      applyQueryFilter(filterKey, queryValues);
      applyQueryFilter('page', FIRST_PAGE);
    },
    [applyQueryFilter],
  );

  const onFiltersApplyAndNavigate = useCallback(
    (pathname: string, filterKey: string, values: string | string[]) => {
      const queryValues =
        typeof values === 'string' ? values : utils.mapArrayToParams(values);

      const params = {
        page: FIRST_PAGE,
        [filterKey]: queryValues,
      } as Record<keyof T, string>;

      setParamsAndNavigate(pathname, params);
    },
    [setParamsAndNavigate],
  );

  const resetFilters = useCallback(
    (fields: string[]) => {
      removeSeveralQueryParams(fields);
      setQueryParam('page', FIRST_PAGE);
    },
    [removeSeveralQueryParams, setQueryParam],
  );

  return {
    params,
    activeFilter,
    hasActiveFilter,
    onFiltersApply,
    applyQueryFilter,
    resetFilters,
    onFiltersApplyAndNavigate,
  };
}
